import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

const K = 8.99e9; // Coulomb's constant

export default function CoulombsLawCalculator() {
  const [charge1, setCharge1] = useState("");

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Physics", url: "https://calclab.app/physics" },
      { name: "Coulomb's Law Calculator", url: "https://calclab.app/calculator/coulombs-law" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Coulomb's Law Calculator",
      "Calculate electrostatic force between charged particles. Free online physics calculator for electric force.",
      "https://calclab.app/calculator/coulombs-law",
      lang
    )
  ]);
  const [charge2, setCharge2] = useState("");
  const [distance, setDistance] = useState("");
  const [result, setResult] = useState<number | null>(null);

  const calculateForce = () => {
    const q1 = parseFloat(charge1);
    const q2 = parseFloat(charge2);
    const r = parseFloat(distance);

    if (isNaN(q1) || isNaN(q2) || isNaN(r) || r === 0) {
      toast.error("Please enter valid values (distance must be non-zero)");
      return;
    }

    const force = K * Math.abs(q1 * q2) / (r * r);
    setResult(force);
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setCharge1("");
    setCharge2("");
    setDistance("");
    setResult(null);
  };

  return (
    <PageLayout calculatorName="Coulomb's Law Calculator" calculatorPath="/calculator/coulombs-law">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Coulomb's Law Calculator</h1>
          <p className="text-muted-foreground">
            Calculate electrostatic force between charges
          </p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Formula: F = k|q₁q₂|/r²</CardTitle>
            <CardDescription>
              Where k = 8.99×10⁹ N·m²/C², q is charge (C), r is distance (m)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="charge1">Charge 1 (C)</Label>
                <Input
                  id="charge1"
                  type="number"
                  step="any"
                  placeholder="Enter charge in Coulombs (e.g., 1e-6)"
                  value={charge1}
                  onChange={(e) => setCharge1(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">Use scientific notation: 1 µC = 1e-6 C</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="charge2">Charge 2 (C)</Label>
                <Input
                  id="charge2"
                  type="number"
                  step="any"
                  placeholder="Enter charge in Coulombs (e.g., 2e-6)"
                  value={charge2}
                  onChange={(e) => setCharge2(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="distance">Distance (m)</Label>
                <Input
                  id="distance"
                  type="number"
                  step="0.01"
                  placeholder="Enter distance between charges"
                  value={distance}
                  onChange={(e) => setDistance(e.target.value)}
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={calculateForce} className="flex-1">
                <Calculator className="mr-2 h-4 w-4" />
                Calculate
              </Button>
              <Button onClick={reset} variant="outline">
                Reset
              </Button>
            </div>

            {result !== null && (
              <>
                <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                  <CardHeader>
                    <CardTitle className="text-xl">Result</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-primary">
                      {result.toExponential(3)} N
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Electrostatic Force
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {result >= 0 ? "Repulsive (same sign)" : "Attractive (opposite sign)"}
                    </p>
                  </CardContent>
                </Card>
                
                <CalculatorSuperCalcCTA config={calculatorCTAs["coulombs-law"]} trigger={result} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">Key Concepts</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>• Coulomb's constant: k = 8.99×10⁹ N·m²/C²</p>
                <p>• Like charges repel, opposite charges attract</p>
                <p>• Force decreases with square of distance</p>
                <p>• 1 µC (microcoulomb) = 1×10⁻⁶ C</p>
                <p className="pt-2 font-medium text-foreground">
                  Example: Two 1 µC charges 1 m apart = 8.99 mN force
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
